import type { AssetKey, PhaseKey } from '$lib/types';
import { triggerPreferencesSync } from './persistenceCoordinator';

export type AssetFilter = 'all' | AssetKey;
export type PhaseFilter = 'all' | PhaseKey;
export type PresetFilter = 'balanced' | 'max_capacity' | 'high_confidence' | 'near_cabinet';

export interface FilterState {
  search: string;
  preset: PresetFilter;
  asset: AssetFilter;
  phase: PhaseFilter;
  minArea: number;
  highOnly: boolean;
  noRoof: boolean;
}

const DEFAULTS: FilterState = {
  search: '',
  preset: 'balanced',
  asset: 'all',
  phase: 'all',
  minArea: 0,
  highOnly: false,
  noRoof: false,
};

function initialFilters(): FilterState {
  if (typeof window !== 'undefined') {
    try {
      const raw = localStorage.getItem('ph-filters');
      if (raw) {
        const saved = JSON.parse(raw) as Partial<FilterState>;
        return {
          search: typeof saved.search === 'string' ? saved.search : DEFAULTS.search,
          preset:
            saved.preset === 'balanced' ||
            saved.preset === 'max_capacity' ||
            saved.preset === 'high_confidence' ||
            saved.preset === 'near_cabinet'
              ? saved.preset
              : DEFAULTS.preset,
          asset:
            saved.asset === 'all' ||
            saved.asset === 'solar' ||
            saved.asset === 'cabinet' ||
            saved.asset === 'both'
              ? saved.asset
              : DEFAULTS.asset,
          phase:
            saved.phase === 'all' ||
            saved.phase === 'existing' ||
            saved.phase === 'planned' ||
            saved.phase === 'next'
              ? saved.phase
              : DEFAULTS.phase,
          minArea:
            typeof saved.minArea === 'number' && Number.isFinite(saved.minArea)
              ? saved.minArea
              : DEFAULTS.minArea,
          highOnly: typeof saved.highOnly === 'boolean' ? saved.highOnly : DEFAULTS.highOnly,
          noRoof: typeof saved.noRoof === 'boolean' ? saved.noRoof : DEFAULTS.noRoof,
        };
      }
    } catch {
      /* ignore */
    }
  }
  return { ...DEFAULTS };
}

const initial = initialFilters();
let search = $state(initial.search);
let preset = $state<PresetFilter>(initial.preset);
let asset = $state<AssetFilter>(initial.asset);
let phase = $state<PhaseFilter>(initial.phase);
let minArea = $state(initial.minArea);
let highOnly = $state(initial.highOnly);
let noRoof = $state(initial.noRoof);

function persist(): void {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(
        'ph-filters',
        JSON.stringify({ search, preset, asset, phase, minArea, highOnly, noRoof }),
      );
    } catch {
      /* ignore */
    }
  }
  triggerPreferencesSync();
}

export const filters = {
  get search() {
    return search;
  },
  set search(v: string) {
    search = v;
    persist();
  },
  get preset() {
    return preset;
  },
  set preset(v: PresetFilter) {
    preset = v;
    persist();
  },
  get asset() {
    return asset;
  },
  set asset(v: AssetFilter) {
    asset = v;
    persist();
  },
  get phase() {
    return phase;
  },
  set phase(v: PhaseFilter) {
    phase = v;
    persist();
  },
  get minArea() {
    return minArea;
  },
  set minArea(v: number) {
    minArea = v;
    persist();
  },
  get highOnly() {
    return highOnly;
  },
  set highOnly(v: boolean) {
    highOnly = v;
    persist();
  },
  get noRoof() {
    return noRoof;
  },
  set noRoof(v: boolean) {
    noRoof = v;
    persist();
  },

  /** Count of non-default filters (for the mobile Filters badge). */
  get activeCount() {
    let n = 0;
    if (asset !== 'all') n++;
    if (phase !== 'all') n++;
    if (minArea > 0) n++;
    if (highOnly) n++;
    if (noRoof) n++;
    return n;
  },

  get rawFilters(): FilterState {
    return { search, preset, asset, phase, minArea, highOnly, noRoof };
  },

  applyFilters(saved: Partial<FilterState>): void {
    if (typeof saved.search === 'string') search = saved.search;
    if (
      saved.preset === 'balanced' ||
      saved.preset === 'max_capacity' ||
      saved.preset === 'high_confidence' ||
      saved.preset === 'near_cabinet'
    ) {
      preset = saved.preset;
    }
    if (
      saved.asset === 'all' ||
      saved.asset === 'solar' ||
      saved.asset === 'cabinet' ||
      saved.asset === 'both'
    ) {
      asset = saved.asset;
    }
    if (
      saved.phase === 'all' ||
      saved.phase === 'existing' ||
      saved.phase === 'planned' ||
      saved.phase === 'next'
    ) {
      phase = saved.phase;
    }
    if (typeof saved.minArea === 'number' && Number.isFinite(saved.minArea)) {
      minArea = saved.minArea;
    }
    if (typeof saved.highOnly === 'boolean') {
      highOnly = saved.highOnly;
    }
    if (typeof saved.noRoof === 'boolean') {
      noRoof = saved.noRoof;
    }
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(
          'ph-filters',
          JSON.stringify({ search, preset, asset, phase, minArea, highOnly, noRoof }),
        );
      } catch {
        /* ignore */
      }
    }
  },

  reset() {
    search = DEFAULTS.search;
    preset = DEFAULTS.preset;
    asset = DEFAULTS.asset;
    phase = DEFAULTS.phase;
    minArea = DEFAULTS.minArea;
    highOnly = DEFAULTS.highOnly;
    noRoof = DEFAULTS.noRoof;
    persist();
  },
};
